import React, { useState } from 'react';
import { Plus, Minus, ShoppingBag, Tag, AlertTriangle, X, Package } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

export const ProductCard = ({ product }) => {
  const { cartItems, addToCart, updateQuantity } = useCart();
  const [isAdding, setIsAdding] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  
  const cartItem = cartItems.find(item => item.id === product.id);
  const quantity = cartItem?.quantity || 0;
  const hasDiscount = product.discount && product.discount > 0;
  const originalPrice = product.price;
  const finalPrice = hasDiscount ? product.price * (1 - product.discount / 100) : product.price;
  const maxQuantity = product.stock || 0;
  const isOutOfStock = !product.stock || product.stock <= 0;
  const isLowStock = product.stock > 0 && product.stock <= 10;

  // Unit display
  const getUnitDisplay = (unit) => {
    const unitMap = {
      'un': 'Unidade',
      'kg': 'Quilograma (kg)',
      'l': 'Litro (L)',
      'ml': 'Mililitro (ml)',
      'pct': 'Pacote',
      'cx': 'Caixa',
      'saco': 'Saco'
    };
    return unitMap[unit] || unit;
  };

  const handleAdd = () => {
    if (isOutOfStock) {
      toast.error('Produto sem estoque');
      return;
    }
    if (quantity >= maxQuantity) {
      toast.warning(`Estoque máximo: ${maxQuantity} unidades`);
      return;
    }
    setIsAdding(true);
    addToCart(product, maxQuantity);
    toast.success(`${product.name} adicionado ao carrinho!`);
    setTimeout(() => setIsAdding(false), 300);
  };

  const handleIncrement = () => {
    if (quantity >= maxQuantity) {
      toast.warning(`Estoque máximo: ${maxQuantity} unidades`);
      return;
    }
    updateQuantity(product.id, quantity + 1, maxQuantity);
  };

  const handleDecrement = () => {
    updateQuantity(product.id, quantity - 1, maxQuantity);
  };

  return (
    <>
      <div 
        className={`bg-card rounded-xl border border-border/50 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group ${isOutOfStock ? 'opacity-70' : ''}`}
        data-testid={`product-card-${product.id}`}
      >
        {/* Image - Clickable */}
        <div 
          className="relative aspect-square bg-muted overflow-hidden cursor-pointer"
          onClick={() => setShowDetails(true)}
        >
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          
          {/* Discount Badge */}
          {hasDiscount && !isOutOfStock && (
            <div className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
              <Tag className="w-3 h-3" />
              -{product.discount}%
            </div>
          )}
          
          {/* Out of Stock Badge */}
          {isOutOfStock && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <span className="bg-red-600 text-white text-sm font-bold px-4 py-2 rounded-full">
                SEM ESTOQUE
              </span>
            </div>
          )}
          
          {/* Low Stock Badge - Esgotando */}
          {isLowStock && !isOutOfStock && (
            <div className="absolute top-2 right-2 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1 animate-pulse">
              <AlertTriangle className="w-3 h-3" />
              Esgotando! ({product.stock})
            </div>
          )}

          {/* Click to view details hint */}
          <div className="absolute bottom-2 left-2 right-2 bg-black/60 text-white text-xs py-1 px-2 rounded text-center opacity-0 group-hover:opacity-100 transition-opacity">
            Clique para ver detalhes
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          <h3 
            className="font-bold text-foreground text-sm md:text-base line-clamp-2 min-h-[2.5rem] cursor-pointer hover:text-primary"
            onClick={() => setShowDetails(true)}
          >
            {product.name}
          </h3>
          
          {product.description && (
            <p className="text-muted-foreground text-xs mt-1 line-clamp-1">
              {product.description}
            </p>
          )}

          <div className="flex items-center justify-between mt-3">
            <div>
              {hasDiscount && (
                <span className="text-sm text-muted-foreground line-through mr-2">
                  R$ {originalPrice.toFixed(2).replace('.', ',')}
                </span>
              )}
              <span className={`text-xl md:text-2xl font-black ${hasDiscount ? 'text-red-500' : 'text-primary'}`}>
                R$ {finalPrice.toFixed(2).replace('.', ',')}
              </span>
              <span className="text-xs text-muted-foreground ml-1">/{product.unit || 'un'}</span>
            </div>
          </div>

          {/* Add to Cart Actions */}
          <div className="mt-4">
            {isOutOfStock ? (
              <Button
                disabled
                className="w-full bg-gray-400 text-white font-bold rounded-full cursor-not-allowed"
              >
                Indisponível
              </Button>
            ) : quantity === 0 ? (
              <Button
                onClick={handleAdd}
                className={`w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold rounded-full transition-all ${
                  isAdding ? 'animate-bounce-subtle' : ''
                }`}
                data-testid={`add-to-cart-${product.id}`}
              >
                <ShoppingBag className="w-4 h-4 mr-2" />
                Adicionar
              </Button>
            ) : (
              <div className="flex items-center justify-between bg-muted rounded-full p-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full hover:bg-destructive hover:text-white"
                  onClick={handleDecrement}
                  data-testid={`decrement-${product.id}`}
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="font-bold text-lg px-4" data-testid={`quantity-${product.id}`}>
                  {quantity}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full hover:bg-primary hover:text-white"
                  onClick={handleIncrement}
                  disabled={quantity >= maxQuantity}
                  data-testid={`increment-${product.id}`}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Product Details Modal */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl">{product.name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Product Image */}
            <div className="relative aspect-video rounded-xl overflow-hidden bg-muted">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover"
              />
              {hasDiscount && (
                <div className="absolute top-3 left-3 bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-full">
                  -{product.discount}% OFF
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="space-y-3">
              {/* Description */}
              <div>
                <h4 className="font-semibold text-sm text-muted-foreground">Descrição</h4>
                <p className="text-foreground">{product.description || 'Sem descrição disponível.'}</p>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-muted/50 rounded-lg p-3">
                  <h4 className="font-semibold text-xs text-muted-foreground">Categoria</h4>
                  <p className="font-medium capitalize">{product.category}</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-3">
                  <h4 className="font-semibold text-xs text-muted-foreground">Unidade</h4>
                  <p className="font-medium">{getUnitDisplay(product.unit)}</p>
                </div>
              </div>

              {/* Stock Status */}
              <div className={`rounded-lg p-3 ${isOutOfStock ? 'bg-red-50 border border-red-200' : isLowStock ? 'bg-orange-50 border border-orange-200' : 'bg-green-50 border border-green-200'}`}>
                <div className="flex items-center gap-2">
                  <Package className={`w-5 h-5 ${isOutOfStock ? 'text-red-600' : isLowStock ? 'text-orange-600' : 'text-green-600'}`} />
                  <div>
                    <h4 className={`font-semibold text-sm ${isOutOfStock ? 'text-red-600' : isLowStock ? 'text-orange-600' : 'text-green-600'}`}>
                      {isOutOfStock ? 'Produto Indisponível' : isLowStock ? 'Últimas Unidades!' : 'Em Estoque'}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {isOutOfStock ? 'Este produto está temporariamente sem estoque.' : `${product.stock} ${product.stock === 1 ? 'unidade disponível' : 'unidades disponíveis'}`}
                    </p>
                  </div>
                </div>
              </div>

              {/* Price */}
              <div className="flex items-center justify-between pt-2 border-t">
                <div>
                  {hasDiscount && (
                    <span className="text-lg text-muted-foreground line-through mr-2">
                      R$ {originalPrice.toFixed(2).replace('.', ',')}
                    </span>
                  )}
                  <span className={`text-3xl font-black ${hasDiscount ? 'text-red-500' : 'text-primary'}`}>
                    R$ {finalPrice.toFixed(2).replace('.', ',')}
                  </span>
                  <span className="text-sm text-muted-foreground ml-1">/{product.unit || 'un'}</span>
                </div>
              </div>

              {/* Add to Cart in Modal */}
              {!isOutOfStock && (
                <Button
                  onClick={() => {
                    handleAdd();
                    setShowDetails(false);
                  }}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold rounded-full py-6 text-lg"
                >
                  <ShoppingBag className="w-5 h-5 mr-2" />
                  Adicionar ao Carrinho
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
